/**
 * Which CRM window in this browser is allowed to hold the Twilio registration.
 *
 * Twilio routes an inbound `<Client>` leg to an identity, and the identity is the
 * operator's user id - so every CRM tab that registers is another candidate for
 * the same call. Twilio then picks ONE of them, and the operator's phone appears
 * not to ring whenever it picks a tab they are not looking at. That is exactly
 * what happened in production on 2026-08-11: three windows registered within the
 * same second, the incoming card came up on two calls out of eleven, and because
 * the tests alternated between two handsets it read as "calls from that number
 * never ring" rather than "the ring lands in the wrong window".
 *
 * The rule here is one sentence: THE WINDOW THE OPERATOR IS LOOKING AT HOLDS THE
 * LEASE. Three mechanisms keep that true, and each covers a way the previous one
 * fails:
 *
 *  1. **Claim on activity.** Mounting, becoming visible or taking focus claims
 *     the lease and announces it. A claim carries the moment it was made, and the
 *     newest claim wins - so a window that has not been touched yields silently
 *     to the one that just has. Background windows never claim, which is what
 *     stops two windows trading the lease back and forth.
 *  2. **Release on unload.** Closing the holder announces the release, and every
 *     surviving window answers with a claim stamped with ITS last active moment.
 *     The most recently used one wins. Without this, closing the front window
 *     would leave the registration with nobody: the others are all in the
 *     background and none of them would think to claim.
 *  3. **Re-assert while visible.** A window that is visible but does not hold the
 *     lease re-claims on a slow timer. This is the crash net - a holder killed
 *     without `pagehide` never releases, and nothing else would ever notice.
 *
 * `busy` is the one override. A window with a call up keeps the lease even when
 * it loses the vote, because yielding means destroying the `Device` and that
 * would drop the live conversation. The yield is deferred, not cancelled: the
 * window hands over the moment the call ends.
 *
 * No BroadcastChannel (Safari before 15.4) means no coordination is possible, and
 * then every window holds the lease - the behaviour this file replaced. Worse,
 * never broken.
 */
import { useEffect, useRef, useState } from "react";
import { claimWins } from "./deviceLease";
import { markWindowActive, windowClaimedAt, windowId } from "./windowActivity";

/** One channel for the whole browser profile; the CRM is one app per profile. */
const CHANNEL = "crm-voice-lease";

/**
 * How often a visible window that does not hold the lease re-claims it.
 *
 * Long, because this only exists for the case where the holder died without
 * saying so: the two events above cover everything else within a frame of the
 * operator's own action. Twenty seconds is a caller hearing the announcement and
 * a few rings before it recovers, which beats a registration nobody holds.
 */
const REASSERT_MS = 20_000;

/**
 * How often a window that lost the vote mid-call checks whether it may hand over.
 *
 * Short, because between losing the vote and handing over there are two
 * registrations on one identity - the state this whole file exists to prevent.
 */
const YIELD_CHECK_MS = 2_000;

type LeaseMessage =
  /** I am the active window; anything claimed earlier than `at` should yield. */
  | { type: "claim"; id: string; at: number }
  /** I am going away. Whoever was used most recently should take over. */
  | { type: "release"; id: string };

export interface DeviceLeaseOptions {
  /** Coordinate at all. False keeps this window out of the vote entirely. */
  enabled: boolean;
  /**
   * A call is up in THIS window, so the registration must not be torn down.
   * Read through a function rather than a prop so a claim arriving mid-call sees
   * the state as of right now, not as of the last render.
   */
  busy: () => boolean;
}

/**
 * True while this window is the one that should be registered with Twilio.
 *
 * Starts true on purpose. A single-window install - the normal case - has nobody
 * to hear its claim and must not wait for a reply that is never coming.
 */
export function useDeviceLease(opts: DeviceLeaseOptions): boolean {
  const { enabled, busy } = opts;
  const [held, setHeld] = useState(true);
  const busyRef = useRef(busy);
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);
  // The answer as of right now, for the timer below - the effect runs once and
  // cannot see the state it set on a later render.
  const heldRef = useRef(true);
  useEffect(() => {
    heldRef.current = held;
  }, [held]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof BroadcastChannel === "undefined") return;

    // Both from `windowActivity.ts` rather than kept here, because the server
    // half of the lease has to bid with the SAME window id and the SAME claim
    // stamp. Two voters working from two sets of numbers can disagree about who
    // holds the phone, and the way that shows is a telephone nobody is holding.
    const id = windowId();
    const channel = new BroadcastChannel(CHANNEL);
    /** Lost the vote while a call was up: hand over as soon as it ends. */
    let yieldPending = false;

    const claim = () => {
      const claimedAt = markWindowActive();
      yieldPending = false;
      setHeld(true);
      channel.postMessage({ type: "claim", id, at: claimedAt } satisfies LeaseMessage);
    };

    channel.onmessage = (event: MessageEvent<LeaseMessage>) => {
      const msg = event.data;
      if (!msg || msg.id === id) return;

      if (msg.type === "release") {
        // The holder is closing. Everyone re-claims with their own last-active
        // stamp and the newest wins, which is the same vote as always - only
        // triggered by somebody leaving rather than by somebody arriving.
        channel.postMessage({ type: "claim", id, at: windowClaimedAt() } satisfies LeaseMessage);
        setHeld(true);
        return;
      }

      // The vote itself lives in `deviceLease.ts`, where it is tested without a
      // browser: both sides of a claim have to reach the same answer, or the
      // registration ends up with nobody.
      if (!claimWins({ id, at: windowClaimedAt() }, msg)) return;
      if (busyRef.current()) {
        yieldPending = true;
        return;
      }
      setHeld(false);
    };

    const onActive = () => {
      if (document.visibilityState !== "visible") return;
      claim();
    };

    // Mounting counts as activity: a window just opened is the one being used.
    claim();

    document.addEventListener("visibilitychange", onActive);
    window.addEventListener("focus", onActive);

    const onHide = () => {
      // `pagehide` rather than `unload`: it is the only one that fires reliably
      // on a phone, where the tab is frozen rather than closed.
      channel.postMessage({ type: "release", id } satisfies LeaseMessage);
    };
    window.addEventListener("pagehide", onHide);

    // The deferred hand-over. Checked on its own short timer rather than with
    // the re-assert below, because until it fires there are two registrations
    // for the same identity again - the very thing this file exists to prevent.
    // Seconds after the call ends, not tens of seconds.
    const yieldTimer = window.setInterval(() => {
      if (!yieldPending || busyRef.current()) return;
      yieldPending = false;
      setHeld(false);
    }, YIELD_CHECK_MS);

    const timer = window.setInterval(() => {
      // The holder never re-claims, and a window without focus never does
      // either. Both halves matter on two monitors, where two windows are
      // visible at once: without them the pair would trade the registration
      // between themselves every twenty seconds, and a `Device` rebuilt on a
      // timer is a phone that is unreachable for a second at a time, forever.
      // Only one window in the browser has focus, so this settles.
      if (heldRef.current || !document.hasFocus()) return;
      onActive();
    }, REASSERT_MS);

    return () => {
      window.clearInterval(timer);
      window.clearInterval(yieldTimer);
      document.removeEventListener("visibilitychange", onActive);
      window.removeEventListener("focus", onActive);
      window.removeEventListener("pagehide", onHide);
      channel.postMessage({ type: "release", id } satisfies LeaseMessage);
      channel.close();
    };
  }, [enabled]);

  return held;
}
